import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

interface CommentLayoutContextType {
  setScrollContainerRef: (el: HTMLDivElement | null) => void
  anchorPositions: ReadonlyMap<string, number>
  registerAnchorElement: (
    anchorKey: string,
    element: HTMLElement | null
  ) => void
  scrollToAnchor: (anchorKey: string) => void
}

const CommentLayoutContext = createContext<
  CommentLayoutContextType | undefined
>(undefined)

/**
 * Pure context provider for anchor position tracking.
 * The content scroll area attaches itself via `setScrollContainerRef`.
 */
export const CommentLayoutProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const anchorElementsRef = useRef(new Map<string, HTMLElement>())
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  )
  const [anchorPositions, setAnchorPositions] = useState<Map<string, number>>(
    () => new Map()
  )

  const recalcPositions = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const scrollTop = container.scrollTop
    const next = new Map<string, number>()

    for (const [key, el] of anchorElementsRef.current) {
      if (!el.isConnected) continue
      const rect = el.getBoundingClientRect()
      const y = rect.top - containerRect.top + scrollTop
      next.set(key, y)
    }

    setAnchorPositions((prev) => {
      if (prev.size === next.size) {
        let same = true
        for (const [k, v] of next) {
          if (prev.get(k) !== v) {
            same = false
            break
          }
        }
        if (same) return prev
      }
      return next
    })
  }, [])

  const registerAnchorElement = useCallback(
    (anchorKey: string, element: HTMLElement | null) => {
      if (element) {
        anchorElementsRef.current.set(anchorKey, element)
      } else {
        anchorElementsRef.current.delete(anchorKey)
      }
      requestAnimationFrame(recalcPositions)
    },
    [recalcPositions]
  )

  const setScrollContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollContainerRef.current = el
      setScrollContainer(el)
      if (el) requestAnimationFrame(recalcPositions)
    },
    [recalcPositions]
  )

  useEffect(() => {
    if (!scrollContainer) return

    let rafId: number | null = null
    const scheduleRecalc = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(recalcPositions)
    }

    const resizeObserver = new ResizeObserver(scheduleRecalc)
    resizeObserver.observe(scrollContainer)
    scrollContainer.addEventListener('scroll', scheduleRecalc, {
      passive: true,
    })

    scheduleRecalc()

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      scrollContainer.removeEventListener('scroll', scheduleRecalc)
    }
  }, [scrollContainer, recalcPositions])

  const scrollToAnchor = useCallback((anchorKey: string) => {
    const el = anchorElementsRef.current.get(anchorKey)
    if (el?.isConnected) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])

  const value = useMemo<CommentLayoutContextType>(
    () => ({
      setScrollContainerRef,
      anchorPositions,
      registerAnchorElement,
      scrollToAnchor,
    }),
    [
      setScrollContainerRef,
      anchorPositions,
      registerAnchorElement,
      scrollToAnchor,
    ]
  )

  return (
    <CommentLayoutContext.Provider value={value}>
      {children}
    </CommentLayoutContext.Provider>
  )
}

export function useCommentLayout(): CommentLayoutContextType {
  const ctx = useContext(CommentLayoutContext)
  if (!ctx) {
    throw new Error(
      'useCommentLayout must be used within a CommentLayoutProvider'
    )
  }
  return ctx
}

export function useCommentLayoutOptional():
  | CommentLayoutContextType
  | undefined {
  return useContext(CommentLayoutContext)
}
